import { useState, useRef, useCallback, useEffect, FC } from 'react';
import { User, Camera, X, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { v4 as uuidv4 } from 'uuid';
import AvatarCropModal from '@/components/ui/AvatarCropModal';
import {
  getInitials,
  fileToDataUrl,
  validateAvatarFile
} from '@/components/ui/avatarUtils';
import { DRAWER_STYLES } from '@/components/ui/GenericDrawer';
import type { Tables } from '@joshua/types/database.generated';

/**
 * Avatar - Componente reutilizável para exibição e upload de avatar
 */

interface AvatarUploadResult {
  fileId: string;
  url: string;
}

interface AvatarProps {
  imageUrl?: string | null;
  name?: string | null;
  size?: number;
  editable?: boolean;
  entidade?: string | null;
  entidadeId?: string | null;
  bucket?: string;
  folder?: string | null;
  papel?: string;
  onUpload?: (result: AvatarUploadResult) => void;
  onRemove?: () => void;
  isLoading?: boolean;
}

// Deep-join narrow shape for entidade_file + file (B.22b/B.25a)
interface EntidadeFileWithStorage {
  file_id: string;
  file: { nome_storage: string | null } | null;
}

type FileRow = Tables<'file'>;

const Avatar: FC<AvatarProps> = ({
  imageUrl,
  name,
  size = 120,
  editable = false,
  entidade,
  entidadeId,
  bucket = 'documento',
  folder,
  papel = 'avatar',
  onUpload,
  onRemove,
  isLoading: externalLoading = false
}) => {
  // Pasta no storage: usa folder se definido, senão usa entidade
  const storageFolder = folder || entidade;
  const { getAuditFields } = useAuth();
  const { showSuccess, showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(imageUrl ?? null);

  // Atualiza URL quando prop muda
  useEffect(() => {
    setAvatarUrl(imageUrl ?? null);
  }, [imageUrl]);

  const isLoading = externalLoading || isUploading;
  const initials = getInitials(name ?? '');

  // Handler para seleção de arquivo
  const handleFileSelect = useCallback(async (file: File): Promise<void> => {
    const validation = validateAvatarFile(file);
    if (!validation.valid) {
      showError(validation.error ?? 'Arquivo inválido');
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      setSelectedImage(typeof dataUrl === 'string' ? dataUrl : null);
      setShowCropModal(true);
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      showError('Erro ao processar imagem');
    }
  }, [showError]);

  // Click no avatar
  const handleClick = () => {
    if (editable && !isLoading) {
      fileInputRef.current?.click();
    }
  };

  // Input file change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input para permitir selecionar mesmo arquivo novamente
    e.target.value = '';
  };

  // Drag & Drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (editable) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!editable) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Upload após crop
  const handleCropConfirm = async (croppedBlob: Blob): Promise<void> => {
    if (!entidade || !entidadeId) {
      showError('Configuração inválida para upload');
      setShowCropModal(false);
      return;
    }

    setIsUploading(true);

    try {
      // 0. Obter campos de audit (tenant_id, filial_id, created_by)
      const auditFields = getAuditFields();

      // 0.5. Remover avatar antigo se existir
      const { data: existingFileRaw } = await supabase
        .from('entidade_file')
        .select('file_id, file:file_id(nome_storage)')
        .eq('entidade', entidade)
        .eq('entidade_id', entidadeId)
        .eq('papel', papel)
        .is('deleted_at', null)
        .maybeSingle();

      // Single-step narrow for PostgREST deep-join (B.22b/B.25a)
      const existingFile = existingFileRaw as unknown as EntidadeFileWithStorage | null;

      if (existingFile) {

        // Remover do storage
        if (existingFile.file?.nome_storage) {
          await supabase.storage
            .from(bucket)
            .remove([existingFile.file.nome_storage]);
        }

        // Remover entidade_file
        await supabase
          .from('entidade_file')
          .delete()
          .eq('file_id', existingFile.file_id)
          .eq('entidade', entidade)
          .eq('entidade_id', entidadeId);

        // Remover file
        await supabase
          .from('file')
          .delete()
          .eq('id', existingFile.file_id);
      }

      // 1. Gerar nome único para o arquivo
      const fileId = uuidv4();
      const fileName = `avatar_${fileId}.jpg`;
      const storagePath = `${storageFolder}/${fileName}`;

      // 2. Upload para o Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(storagePath, croppedBlob, {
          contentType: 'image/jpeg',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // 3. Obter URL assinada (bucket privado)
      const { data: urlData, error: urlError } = await supabase.storage
        .from(bucket)
        .createSignedUrl(storagePath, 60 * 60 * 24 * 365); // 1 ano

      if (urlError) throw urlError;
      const publicUrl = urlData.signedUrl;

      // 4. Criar registro em file (sem entidade/entidade_id - esses campos estão em entidade_file)
      const filePayload = {
        id: fileId,
        nome_original: 'avatar.jpg',
        nome_storage: storagePath,
        mime_type: 'image/jpeg',
        tamanho_bytes: croppedBlob.size,
        descricao: 'Avatar',
        tipo: 'avatar',
        ...auditFields
      };
      // B.23a mixed-shape Insert: auditFields spread produces dynamic shape
      // that defeats generated types; narrow via unknown double-cast boundary.
      const { data: fileRecord, error: fileError } = await (
        supabase.from('file') as unknown as {
          insert: (p: Record<string, unknown>) => {
            select: () => {
              single: () => Promise<{ data: FileRow | null; error: unknown }>;
            };
          };
        }
      ).insert(filePayload).select().single();

      if (fileError) throw fileError;

      // 5. Criar registro em entidade_file (sem tenant_id - herda de file)
      const { error: junctionError } = await supabase
        .from('entidade_file')
        .insert({
          file_id: fileId,
          entidade: entidade,
          entidade_id: entidadeId,
          papel: papel,
          ordem: 0,
          created_by: auditFields.created_by
        });

      if (junctionError) throw junctionError;

      // 6. Atualizar estado local
      setAvatarUrl(publicUrl);
      showSuccess('Avatar atualizado com sucesso');

      // 7. Callback
      if (onUpload) {
        onUpload({ fileId, url: publicUrl });
      }
    } catch (error) {
      const err = error as { message?: string; details?: string; hint?: string } | null;
      console.error('🖼️ Avatar upload - ERRO:', error);
      console.error('🖼️ Avatar upload - Detalhes:', err?.message, err?.details, err?.hint);
      showError('Erro ao fazer upload do avatar');
    } finally {
      setIsUploading(false);
      setShowCropModal(false);
      setSelectedImage(null);
    }
  };

  // Cancelar crop
  const handleCropCancel = () => {
    setShowCropModal(false);
    setSelectedImage(null);
  };

  // Remover avatar
  const handleRemove = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();

    if (!entidade || !entidadeId) return;

    try {
      setIsUploading(true);

      // Buscar arquivo existente (apenas ativos)
      const { data: existingFileRaw } = await supabase
        .from('entidade_file')
        .select('file_id, file:file_id(nome_storage)')
        .eq('entidade', entidade)
        .eq('entidade_id', entidadeId)
        .eq('papel', papel)
        .is('deleted_at', null)
        .single();

      // Single-step narrow for PostgREST deep-join (B.22b/B.25a)
      const existingFile = existingFileRaw as unknown as EntidadeFileWithStorage | null;

      if (existingFile) {
        // Remover do storage
        if (existingFile.file?.nome_storage) {
          await supabase.storage
            .from(bucket)
            .remove([existingFile.file.nome_storage]);
        }

        // Remover entidade_file
        await supabase
          .from('entidade_file')
          .delete()
          .eq('file_id', existingFile.file_id)
          .eq('entidade', entidade)
          .eq('entidade_id', entidadeId);

        // Remover file
        await supabase
          .from('file')
          .delete()
          .eq('id', existingFile.file_id);
      }

      setAvatarUrl(null);
      showSuccess('Avatar removido');

      if (onRemove) {
        onRemove();
      }
    } catch (error) {
      console.error('Erro ao remover avatar:', error);
      showError('Erro ao remover avatar');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      {/* Container pai para posicionar o botão X fora do círculo */}
      <div
        style={{
          position: 'relative',
          width: size,
          height: size,
          flexShrink: 0
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Avatar Circle */}
        <div
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            overflow: 'hidden',
            cursor: editable ? 'pointer' : 'default',
            border: isDragging
              ? '3px dashed var(--color-primary)'
              : `3px solid ${DRAWER_STYLES.colors.border}`,
            background: avatarUrl ? 'transparent' : '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
          role={editable ? 'button' : undefined}
          tabIndex={editable ? 0 : undefined}
          aria-label={editable ? (avatarUrl ? 'Alterar foto de perfil' : 'Adicionar foto de perfil') : undefined}
          onKeyDown={(e) => {
            if (editable && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleClick();
            }
          }}
        >
          {/* Avatar Image or Initials */}
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name || 'Avatar'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : initials ? (
            <span
              style={{
                color: '#94a3b8',
                fontSize: size * 0.35,
                fontWeight: 600,
                userSelect: 'none'
              }}
            >
              {initials}
            </span>
          ) : (
            <User
              size={size * 0.5}
              color="#94a3b8"
            />
          )}

          {/* Hover Overlay (edit mode) */}
          {editable && (isHovered || isDragging) && !isLoading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px'
              }}
            >
              <Camera size={size * 0.25} color="white" />
              <span style={{ color: 'white', fontSize: '11px', fontWeight: 500 }}>
                {avatarUrl ? 'Alterar' : 'Adicionar'}
              </span>
            </div>
          )}

          {/* Loading Overlay */}
          {isLoading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Loader2
                size={size * 0.3}
                color="white"
                style={{ animation: 'spin 1s linear infinite' }}
              />
            </div>
          )}

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleInputChange}
            style={{ display: 'none' }}
          />
        </div>

        {/* Remove Button - fora do círculo, no container pai */}
        {editable && avatarUrl && isHovered && !isLoading && (
          <button
            onClick={handleRemove}
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: '#dc2626',
              border: '2px solid white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              zIndex: 10
            }}
            title="Remover foto"
          >
            <X size={14} color="white" />
          </button>
        )}
      </div>

      {/* Crop Modal */}
      {showCropModal && selectedImage && (
        <AvatarCropModal
          imageSrc={selectedImage}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          isLoading={isUploading}
        />
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default Avatar;
